import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'
import Loading from '@/shared/Loading'

// Reads the Charge ledger only: money that actually moved, recorded from
// Stripe as it happened. Failed cards and abandoned checkouts are filtered
// server-side; showing someone a charge they were never charged for is worse
// than showing nothing.

const money = (cents) => `$${(cents / 100).toFixed(2)}`

const STATUS_PILL = {
  succeeded: { label: 'Paid', className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  partially_refunded: { label: 'Partly refunded', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
  disputed: { label: 'Disputed', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  reversed: { label: 'Reversed', className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
}

const StatusPill = ({ status }) => {
  const pill = STATUS_PILL[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  return <span className={`text-xs font-medium px-2 py-1 rounded ${pill.className}`}>{pill.label}</span>
}

const BillingHistory = () => {
  const { getRequest } = useQuery()
  const [loading, setLoading] = useState(true)
  const [charges, setCharges] = useState([])

  useEffect(() => {
    getRequest('/api/v1/billing/history', {}, (err, data) => {
      setLoading(false)
      if (err || !data?.charges) return
      setCharges(data.charges)
    })
  }, [])

  if (loading) return <Loading />

  return (
    <div className='max-w-4xl mx-auto py-6'>
      <p className='text-sm text-gray-600 dark:text-gray-400 mb-6'>
        Thank you for supporting Closing Credits. Every enrollment keeps the lights on.
      </p>

      {/* Ledger went live 2026-09. Keep this date in step with the deploy. */}
      <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6'>
        <p className='text-sm text-blue-900 dark:text-blue-200'>
          Payments from before September 2026, and PayPal memberships, aren't listed here.
          If you need one of those receipts, email support@closingcredits.com and we'll dig it out.
        </p>
      </div>

      <Card>
        {charges.length === 0 ? (
          <div className='text-center py-10'>
            <p className='text-gray-900 dark:text-white font-medium mb-1'>Nothing here yet.</p>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              Enroll in a <a href='/courses' className='text-clcpurple hover:underline'>course</a> and this page gets more interesting.
            </p>
          </div>
        ) : (
          <div className='divide-y divide-gray-200 dark:divide-gray-700'>
            {charges.map((charge) => (
              <div key={charge.id} className='py-4 flex items-start justify-between gap-4'>
                <div className='min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-medium text-gray-900 dark:text-white'>{charge.description}</span>
                    <StatusPill status={charge.status} />
                  </div>
                  <div className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                    {new Date(charge.occurred_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                    {charge.payment_method && <> · {charge.payment_method}</>}
                  </div>
                  {charge.refunded_cents > 0 && (
                    <div className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                      Refunded {money(charge.refunded_cents)}{charge.refunded_partially && ' of this charge'}
                    </div>
                  )}
                </div>
                <div className='text-right shrink-0'>
                  <div className='font-bold text-gray-900 dark:text-white'>{money(charge.amount_cents)}</div>
                  {charge.receipt_number && (
                    <Link to={`/settings/billing/receipts/${charge.receipt_number}`} className='text-sm text-clcpurple hover:underline'>
                      Receipt
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

export default BillingHistory
